import { DEFAULT_INVOICE_FONT_ID } from "@common/constants/invoiceDesign";
import { createAllIncludedNativeInvoicePdfFieldSettings } from "@common/helpers/applyNativeInvoicePdfFieldSettings";
import { normalizeNativeInvoicePdfFieldSettings } from "@common/helpers/normalizeNativeInvoicePdfFieldSettings";
import { NATIVE_INVOICE_DEFAULT_PAGE_SIZE } from "@common/constants/nativeInvoice";
import type {
    NativeInvoiceParty,
    NativeInvoiceRenderSnapshot,
} from "@common/schemas/domains/invoice/native-invoice.schema";
import { hasAnyAddressPart } from "./hasAnyAddressPart";
import { storedAdjustmentToLabeled } from "./nativeInvoiceAdjustment";
import { labeledLineAdjustmentFromStored } from "./nativeInvoicePerLineAdjustment";
import {
    DEFAULT_NATIVE_INVOICE_DEPOSIT_SIGN,
    DEFAULT_NATIVE_INVOICE_DISCOUNT_SIGN,
    DEFAULT_NATIVE_INVOICE_TAX_SIGN,
    NativeInvoiceAdjustmentMode,
    NativeInvoiceAdjustmentSign,
    NativeInvoiceCustomer,
    NativeInvoiceFooterAlign,
    NativeInvoicePageSize,
    NativeInvoiceFromState,
    NativeInvoiceHydratedFields,
    NativeInvoiceLineCodingByLineId,
    NativeInvoiceLineRow,
} from "./nativeInvoiceFormTypes";

function resolveAdjustment({
    input,
    computed,
    mode,
    sign,
    defaultSign,
}: {
    input: number | null | undefined;
    computed: number;
    mode: NativeInvoiceAdjustmentMode;
    sign: NativeInvoiceAdjustmentSign;
    defaultSign: NativeInvoiceAdjustmentSign;
}): { value: number; mode: NativeInvoiceAdjustmentMode } {
    // A null `*Input` marks a snapshot that only persisted the resolved
    // monetary amount; fall back to the computed value in that case. The mode
    // is always present now (schema default + NOT NULL column) so it is used
    // as-is. Reconstruct the labeled form value from the persisted sign so
    // both legacy unsigned inputs and new signed inputs hydrate the same way.
    return {
        value: storedAdjustmentToLabeled({
            value: Math.abs(input ?? computed),
            sign,
            defaultSign,
        }),
        mode,
    };
}

function fromStateFromSnapshot(
    from: NativeInvoiceParty,
): NativeInvoiceFromState {
    return {
        businessName: from.businessName ?? "",
        name: from.name ?? "",
        email: from.email ?? "",
        phoneNumber: from.phoneNumber ?? "",
        taxId: from.taxId ?? "",
        street: from.address.street ?? "",
        street2: from.address.street2 ?? "",
        city: from.address.city ?? "",
        state: from.address.state ?? "",
        postalCode: from.address.postalCode ?? "",
        country: from.address.country ?? "",
    };
}

// A persisted null means the user cleared the field, so it must not be
// backfilled from the counterparty (that would resurface removed details on
// the next save). The counterparty fallback only fires when the key is absent
// altogether, i.e. legacy snapshots that predate the field — Zod-validated
// snapshots type these fields as string | null and never yield undefined.
function snapshotFieldOrLegacyFallback({
    snapshotValue,
    legacyFallback,
}: {
    snapshotValue: string | null | undefined;
    legacyFallback: string | null | undefined;
}): string | null {
    return snapshotValue !== undefined
        ? snapshotValue
        : (legacyFallback ?? null);
}

function customerFromSnapshot({
    to,
    counterparty,
}: {
    to: NativeInvoiceParty;
    counterparty: NativeInvoiceCustomer | null | undefined;
}): NativeInvoiceCustomer | null {
    if (!counterparty?.id) {
        // To contact details typed without a linked counterparty are persisted
        // on the snapshot; rehydrate them as the same placeholder customer
        // (empty id) the form creates, so reopening the draft doesn't clear
        // them on the next save. The headline and company are separate fields.
        const hasSnapshotContactDetails =
            Boolean(to.email) ||
            Boolean(to.phoneNumber) ||
            hasAnyAddressPart(to.address);
        if (!hasSnapshotContactDetails) {
            return null;
        }
        return {
            id: "",
            name: "",
            email: to.email ?? null,
            phoneNumber: to.phoneNumber ?? null,
            street: to.address.street ?? null,
            street2: to.address.street2 ?? null,
            city: to.address.city ?? null,
            state: to.address.state ?? null,
            postalCode: to.address.postalCode ?? null,
            country: to.address.country ?? null,
        };
    }
    return {
        id: counterparty.id,
        name: counterparty.name ?? to.businessName ?? "",
        email: snapshotFieldOrLegacyFallback({
            snapshotValue: to.email,
            legacyFallback: counterparty.email,
        }),
        cc: counterparty.cc,
        bcc: counterparty.bcc,
        phoneNumber: snapshotFieldOrLegacyFallback({
            snapshotValue: to.phoneNumber,
            legacyFallback: counterparty.phoneNumber,
        }),
        street: snapshotFieldOrLegacyFallback({
            snapshotValue: to.address.street,
            legacyFallback: counterparty.street,
        }),
        street2: snapshotFieldOrLegacyFallback({
            snapshotValue: to.address.street2,
            legacyFallback: counterparty.street2,
        }),
        city: snapshotFieldOrLegacyFallback({
            snapshotValue: to.address.city,
            legacyFallback: counterparty.city,
        }),
        state: snapshotFieldOrLegacyFallback({
            snapshotValue: to.address.state,
            legacyFallback: counterparty.state,
        }),
        postalCode: snapshotFieldOrLegacyFallback({
            snapshotValue: to.address.postalCode,
            legacyFallback: counterparty.postalCode,
        }),
        country: snapshotFieldOrLegacyFallback({
            snapshotValue: to.address.country,
            legacyFallback: counterparty.country,
        }),
    };
}

function resolveQuantityFromSnapshot(
    line: NativeInvoiceRenderSnapshot["lines"][number],
): number | null {
    if (line.quantity != null) {
        return line.quantity;
    }
    if (line.unitAmount == null && line.amount !== 0) {
        return 1;
    }
    return null;
}

function resolveUnitAmountFromSnapshot(
    line: NativeInvoiceRenderSnapshot["lines"][number],
    quantity: number | null,
): number | null {
    if (line.unitAmount != null) {
        return line.unitAmount;
    }
    if (line.amount === 0) {
        return null;
    }
    if (quantity != null && quantity !== 0) {
        return line.amount / quantity;
    }
    return null;
}

// Exported so callers building a synthetic single-line snapshot object (e.g.
// the create-time ad-hoc prefill in nativeInvoiceFormHelpers.prefillLineToRow)
// reuse the same quantity/unit-amount inference and adjustment hydration as
// editing a persisted invoice, instead of duplicating that logic.
export function lineRowFromSnapshot({
    line,
    generateId,
    codingByLineId,
}: {
    line: NativeInvoiceRenderSnapshot["lines"][number];
    generateId: () => string;
    codingByLineId: NativeInvoiceLineCodingByLineId | undefined;
}): NativeInvoiceLineRow {
    const quantity = resolveQuantityFromSnapshot(line);
    const unitAmount = resolveUnitAmountFromSnapshot(line, quantity);
    const id = line.id ?? generateId();
    const coding = line.id ? codingByLineId?.[line.id] : undefined;
    const lineAmount = line.amount;
    return {
        id,
        catalogItemId: line.catalogItemId ?? null,
        catalogItemPriceId: line.catalogItemPriceId ?? null,
        productOrService: line.productOrService ?? "",
        description: line.description ?? "",
        quantity,
        unitAmount,
        classIds: coding?.classIds ?? [],
        accrualAccountId: coding?.accrualAccountId ?? null,
        receivablesAccountId: coding?.receivablesAccountId ?? null,
        tax: labeledLineAdjustmentFromStored({
            storedAmount: line.tax,
            mode: line.taxMode,
            lineAmount,
        }),
        discount: labeledLineAdjustmentFromStored({
            storedAmount: line.discount,
            mode: line.discountMode,
            lineAmount,
        }),
        deposit: labeledLineAdjustmentFromStored({
            storedAmount: line.deposit ?? 0,
            mode: line.depositMode,
            lineAmount,
        }),
        billableExpenseTransactionId: line.billableExpenseTransactionId ?? null,
    };
}

function footerAlignFromSnapshot(
    align: NativeInvoiceRenderSnapshot["footerAlign"],
): NativeInvoiceFooterAlign {
    if (align === "left" || align === "center" || align === "right") {
        return align;
    }
    return "center";
}

function pageSizeFromSnapshot(
    pageSize: NativeInvoiceRenderSnapshot["pageSize"],
): NativeInvoicePageSize {
    if (pageSize === "letter" || pageSize === "a4") {
        return pageSize;
    }
    // Legacy snapshots (saved before the field existed) keep the render
    // fallback (a4), not the new-draft default (letter) — otherwise editing
    // them would silently change their page box.
    return NATIVE_INVOICE_DEFAULT_PAGE_SIZE;
}

/**
 * Maps a persisted native-invoice render snapshot into the editable form
 * fields (lines, adjustments, parties, dates, design). Line Class / Accrual /
 * Receivables coding is merged from `lineCodingByLineId` (invoice lines), not
 * the snapshot. Pure so it can be unit-tested without mounting the drawer hook.
 */
export function hydrateNativeInvoiceFormFromSnapshot({
    snapshot,
    counterparty,
    logoPreviewUrl,
    lineCodingByLineId,
    generateId,
}: {
    snapshot: NativeInvoiceRenderSnapshot;
    counterparty?: NativeInvoiceCustomer | null;
    logoPreviewUrl?: string | null;
    lineCodingByLineId?: NativeInvoiceLineCodingByLineId;
    generateId: () => string;
}): NativeInvoiceHydratedFields {
    const from = fromStateFromSnapshot(snapshot.from);
    const customer = customerFromSnapshot({
        to: snapshot.to,
        counterparty,
    });
    const tax = resolveAdjustment({
        input: snapshot.taxInput,
        computed: snapshot.tax,
        mode: snapshot.taxMode,
        sign: snapshot.taxSign,
        defaultSign: DEFAULT_NATIVE_INVOICE_TAX_SIGN,
    });
    const discount = resolveAdjustment({
        input: snapshot.discountInput,
        computed: snapshot.discount,
        mode: snapshot.discountMode,
        sign: snapshot.discountSign,
        defaultSign: DEFAULT_NATIVE_INVOICE_DISCOUNT_SIGN,
    });
    const deposit = resolveAdjustment({
        input: snapshot.depositInput,
        computed: snapshot.deposit,
        mode: snapshot.depositMode,
        sign: snapshot.depositSign,
        defaultSign: DEFAULT_NATIVE_INVOICE_DEPOSIT_SIGN,
    });

    const toContactName = snapshot.to.name ?? "";

    return {
        from,
        customer,
        toTaxId: snapshot.to.taxId ?? "",
        // Hydrate the Contact name verbatim, even when it matches the display
        // name. Contact name and Company are distinct fields; the PDF render
        // dedupes an identical raw name against the headline so it never
        // prints twice (see resolvePartyNameLine).
        toFullName: toContactName,
        toCompany: snapshot.to.company ?? "",
        invoiceNumber: snapshot.invoiceNumber ?? "",
        invoiceDate: snapshot.invoiceDate,
        dueDate: snapshot.dueDate ?? "",
        poNumber: snapshot.poNumber ?? "",
        note: snapshot.note ?? "",
        terms: snapshot.terms ?? "",
        footer: snapshot.footer ?? "",
        footerAlign: footerAlignFromSnapshot(snapshot.footerAlign),
        showPagination: snapshot.showPagination ?? true,
        pageSize: pageSizeFromSnapshot(snapshot.pageSize),
        tax: tax.value,
        discount: discount.value,
        deposit: deposit.value,
        taxMode: tax.mode,
        discountMode: discount.mode,
        depositMode: deposit.mode,
        isTaxPerLineItem: snapshot.isTaxPerLineItem,
        isDiscountPerLineItem: snapshot.isDiscountPerLineItem,
        isDepositPerLineItem: snapshot.isDepositPerLineItem,
        isTaxDisplayedPerLineItem: snapshot.isTaxDisplayedPerLineItem,
        isDiscountDisplayedPerLineItem: snapshot.isDiscountDisplayedPerLineItem,
        isDepositDisplayedPerLineItem: snapshot.isDepositDisplayedPerLineItem,
        logoPath: snapshot.logoPath ?? null,
        logoPreviewUrl: snapshot.logoPath ? (logoPreviewUrl ?? null) : null,
        backgroundColor: snapshot.backgroundColor ?? null,
        accentColor: snapshot.accentColor ?? null,
        textColor: snapshot.textColor ?? null,
        fontFamily: snapshot.fontFamily ?? DEFAULT_INVOICE_FONT_ID,
        labelOverrides: snapshot.labelOverrides ?? null,
        fieldSettings: snapshot.fieldSettings
            ? normalizeNativeInvoicePdfFieldSettings(snapshot.fieldSettings)
            : createAllIncludedNativeInvoicePdfFieldSettings(),
        lines: snapshot.lines.map((line) =>
            lineRowFromSnapshot({
                line,
                generateId,
                codingByLineId: lineCodingByLineId,
            }),
        ),
    };
}
